// Request/response schemas for the schools tenancy surface (R9).
// docs/replan-v2.md.
//
// Covers: school-admin signup, teacher invites + accept, student provisioning,
// classes + membership, assignments, and the teacher progress view.
// Reuses the auth primitives (email/username/password) so the username
// uniqueness + password policies stay identical to the family side.

import { z } from 'zod';

import {
  emailSchema,
  kidPasswordSchema,
  localeSchema,
  parentPasswordSchema,
  usernameSchema,
} from './auth';

const gradeSchema = z.enum(['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6']);

// ─── School-admin signup ─────────────────────────────────────────────────────
// A separate flow from family register: creates the school + the admin user in
// one go. Audited under a distinct action so the school-onboarding funnel is
// separable from family signups.

export const schoolRegisterInput = z.object({
  email: emailSchema,
  password: parentPasswordSchema,
  adminDisplayName: z.string().trim().min(1).max(120),
  schoolName: z.string().trim().min(2).max(120),
  country: z.string().length(2).toUpperCase(),
  locale: localeSchema.optional().default('ar'),
  marketingOptIn: z.boolean().optional().default(false),
});
export type SchoolRegisterInput = z.infer<typeof schoolRegisterInput>;

// ─── Teacher invites ─────────────────────────────────────────────────────────

export const teacherInviteInput = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(1).max(120),
});
export type TeacherInviteInput = z.infer<typeof teacherInviteInput>;

// The teacher follows the emailed link and sets a password. The token carries
// the school + intended email, so only a password is needed here.
export const teacherInviteAcceptInput = z.object({
  token: z.string().min(20).max(200),
  password: parentPasswordSchema,
  displayName: z.string().trim().min(1).max(120).optional(),
});
export type TeacherInviteAcceptInput = z.infer<typeof teacherInviteAcceptInput>;

export const teacherInvitePreviewQuery = z.object({
  token: z.string().min(20).max(200),
});
export type TeacherInvitePreviewQuery = z.infer<typeof teacherInvitePreviewQuery>;

// ─── Student provisioning (teacher-issued, like parent→kid) ──────────────────
// Students sign in on shared school devices, so the teacher picks the username
// + initial password directly rather than emailing the student.

export const studentCreateInput = z.object({
  username: usernameSchema,
  password: kidPasswordSchema,
  displayName: z.string().trim().min(1).max(120),
  avatar: z.string().trim().min(1).max(60).optional().default('star'),
  grade: gradeSchema.nullable().optional(),
  localePref: localeSchema.optional().default('ar'),
  tashkeelMode: z.enum(['with', 'partial', 'without']).optional().default('with'),
  // Optionally drop the new student straight into a class.
  classId: z.uuid().nullable().optional(),
});
export type StudentCreateInput = z.infer<typeof studentCreateInput>;

export const studentResetPasswordInput = z.object({
  password: kidPasswordSchema,
});
export type StudentResetPasswordInput = z.infer<typeof studentResetPasswordInput>;

// ─── Classes ─────────────────────────────────────────────────────────────────

export const classCreateInput = z.object({
  name: z.string().trim().min(1).max(120),
  grade: gradeSchema,
  // Assign a teacher at create time (must be a teacher in this school).
  teacherUserId: z.uuid().nullable().optional(),
});
export type ClassCreateInput = z.infer<typeof classCreateInput>;

export const classUpdateInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  grade: gradeSchema.optional(),
  teacherUserId: z.uuid().nullable().optional(),
});
export type ClassUpdateInput = z.infer<typeof classUpdateInput>;

export const classMemberInput = z.object({
  userId: z.uuid(),
});
export type ClassMemberInput = z.infer<typeof classMemberInput>;

// ─── Assignments ─────────────────────────────────────────────────────────────

export const assignmentCreateInput = z.object({
  classId: z.uuid(),
  lessonId: z.uuid(),
  dueAt: z.iso.datetime().nullable().optional(),
  instructionsKey: z.string().trim().max(240).nullable().optional(),
});
export type AssignmentCreateInput = z.infer<typeof assignmentCreateInput>;

// ─── Response DTOs ───────────────────────────────────────────────────────────

export const schoolDto = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  country: z.string(),
  locale: localeSchema,
  status: z.string(),
});
export type SchoolDto = z.infer<typeof schoolDto>;

export const teacherDto = z.object({
  id: z.uuid(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  status: z.string(), // school_members.status: 'active' (joined) | 'invited'
  invitedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
});
export type TeacherDto = z.infer<typeof teacherDto>;

export const studentDto = z.object({
  id: z.uuid(),
  username: z.string(),
  displayName: z.string().nullable(),
  avatar: z.string().nullable(),
  grade: z.string().nullable(),
  localePref: localeSchema,
  createdAt: z.string(),
});
export type StudentDto = z.infer<typeof studentDto>;

export const classDto = z.object({
  id: z.uuid(),
  name: z.string(),
  grade: z.string(),
  teacher: z.object({ id: z.uuid(), displayName: z.string().nullable() }).nullable(),
  studentCount: z.number(),
});
export type ClassDto = z.infer<typeof classDto>;

export const assignmentDto = z.object({
  id: z.uuid(),
  classId: z.uuid(),
  lessonId: z.uuid(),
  lessonTitleKey: z.string(),
  dueAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AssignmentDto = z.infer<typeof assignmentDto>;

export const teacherInvitePreviewDto = z.object({
  schoolName: z.string(),
  email: z.string(),
  valid: z.boolean(),
});
export type TeacherInvitePreviewDto = z.infer<typeof teacherInvitePreviewDto>;
